import React, { useState, useEffect, useRef, useCallback } from "react"
import { makeStyles } from "tss-react/mui"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  FormControlLabel,
  Switch,
  Tooltip,
  Collapse,
} from "@mui/material"
import {
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Refresh as RefreshIcon,
  GraphicEq as GraphicEqIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material"
import { isIOS } from "../utils/platform"
import MicLevelMeter from "./MicLevelMeter"
import MicProcessingPanel from "./MicProcessingPanel"
import voiceChain, { createSelfListenMonitor } from "../services/voiceChain"

const useStyles = makeStyles()((theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    minWidth: 400,
    maxWidth: 460,
  },
  // 16:9 preview frame with the live camera (or an "off" placeholder).
  preview: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%",
    backgroundColor: "#000",
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    marginBottom: theme.spacing(2),
  },
  previewVideo: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    // NOT mirrored — the preview must match what others actually receive, which
    // is what the sidebar self-monitor + grid "You" tile show (un-mirrored).
  },
  previewPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  // "Start with … on entry" toggle rows, shown under the device dropdowns.
  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  toggleLabel: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  toggleTitle: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  toggleSub: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  deviceRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  deviceIcon: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  select: {
    flex: 1,
    minWidth: 0,
  },
  refreshBtn: {
    flexShrink: 0,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(1),
  },
  advancedToggle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 0),
    cursor: "pointer",
    userSelect: "none",
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: 0.3,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
    },
  },
  advancedBody: {
    paddingBottom: theme.spacing(1),
  },
  accessNote: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
}))

// Stop every track on a stream (releases the camera/mic). Safe on null.
const stopStream = (stream) => {
  if (!stream) return
  stream.getTracks().forEach((t) => t.stop())
}

// Bind a MediaStream to the preview <video>. srcObject can't be set via JSX.
const PreviewVideo = ({ stream, className }) => {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (el && el.srcObject !== stream) el.srcObject = stream || null
  }, [stream])
  return <video ref={ref} className={className} autoPlay playsInline muted />
}

/**
 * Pre-join device/stream setup modal. Lets the user preview their camera, pick
 * a camera + microphone, and choose whether to enter with video/mic on, before
 * joining the room. The chosen settings are returned via onConfirm and persisted
 * by the parent (App) for reuse on future entry.
 *
 * Props:
 *  - open
 *  - onClose()                : dismiss without applying (enter with webcam off)
 *  - onConfirm(settings)      : { videoDeviceId, audioDeviceId, videoOn, micOn, dontShowAgain }
 *  - isGuest                  : guests can't suppress the modal
 *  - initialSettings          : saved settings to pre-fill (or null)
 *  - showDontShowAgain        : whether to render the "Don't show again" checkbox
 *  - reopened                 : true when reopened mid-session (gear icon) vs the
 *                               pre-join prompt — switches the dismiss button from
 *                               "Skip" to "Cancel" (no join is pending to skip).
 *  - isPublishing             : true when the user is already publishing live (the
 *                               modal was reopened via the gear while on cam).
 *  - activeStream             : the live self-monitor stream when publishing. On
 *                               iOS we PREVIEW THIS rather than opening a second
 *                               getUserMedia — iOS allows only one active camera,
 *                               so a fresh preview capture would steal the camera
 *                               from the live producer and drop every watcher
 *                               before the user even confirms a switch.
 */
const DeviceSetupModal = ({
  open,
  onClose,
  onConfirm,
  isGuest = false,
  initialSettings = null,
  showDontShowAgain = true,
  reopened = false,
  isPublishing = false,
  activeStream = null,
  // Vocal-chain settings for the collapsed "Advanced" section (mirrors the
  // Settings → Voice & Video panel). Section hidden when not wired.
  micProcessing = null,
  onMicProcessingChange = null,
}) => {
  const { classes } = useStyles()
  // "Advanced" (mic processing) disclosure — collapsed on every open.
  const [advancedOpen, setAdvancedOpen] = useState(false)
  useEffect(() => {
    if (!open) setAdvancedOpen(false)
  }, [open])

  // Self-listen monitor for the quick meter row. Built on toggle, disposed on
  // stop/unmount (MicLevelMeter guarantees onListenStop fires either way).
  const listenMonitorRef = useRef(null)
  const handleQuickListenStop = useCallback(() => {
    if (listenMonitorRef.current) {
      listenMonitorRef.current.dispose() // stops capture + restores outbound mic
      listenMonitorRef.current = null
    }
  }, [])

  const [videoInputs, setVideoInputs] = useState([])
  const [audioInputs, setAudioInputs] = useState([])
  const [videoDeviceId, setVideoDeviceId] = useState("")
  const [audioDeviceId, setAudioDeviceId] = useState("")
  const [videoOn, setVideoOn] = useState(true)
  const [micOn, setMicOn] = useState(true)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [previewStream, setPreviewStream] = useState(null)
  const [error, setError] = useState(null)

  // Quick-meter self-listen: EC-free monitor on the currently-selected mic,
  // processed with the user's chain settings so it previews what others hear.
  const handleQuickListen = useCallback(async () => {
    const monitor = await createSelfListenMonitor({
      audioDeviceId: audioDeviceId || "",
      settings: micProcessing || null,
      limiterEnabled: voiceChain.limiterEnabled,
    })
    listenMonitorRef.current = monitor
    return monitor.stream
  }, [audioDeviceId, micProcessing])

  // Holds the live preview stream so we can stop it on re-acquire / close
  // without depending on render state (avoids stale-closure leaks).
  const previewRef = useRef(null)

  const setPreview = (stream) => {
    previewRef.current = stream
    setPreviewStream(stream)
  }

  // iOS-while-publishing: we must NOT open a fresh preview getUserMedia (it would
  // steal the single iOS camera from the live producer and drop watchers). Show
  // the live self-stream instead and defer the actual device switch to confirm
  // (handleSwapDevices in App). Desktop and the not-yet-publishing pre-join case
  // keep the normal live-preview behaviour.
  const iosLive = isIOS() && isPublishing && !!activeStream

  // True only for a stream WE acquired (so release() may stop it). The borrowed
  // activeStream is owned by mediaService and must never be stopped here.
  const ownsPreviewRef = useRef(false)

  // (Re)acquire the preview stream for the given selection. Video is requested
  // only when videoOn (so toggling off actually turns the camera light off).
  // Audio is always requested so the mic stays selectable and labels stay
  // populated. Falls back to non-exact constraints if a saved deviceId is stale.
  const acquire = useCallback(async ({ videoDeviceId, audioDeviceId, videoOn, strictAudio }) => {
    stopStream(previewRef.current)
    setPreview(null)
    setError(null)
    const build = (exact) => ({
      video: videoOn
        ? {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            ...(exact && videoDeviceId ? { deviceId: { exact: videoDeviceId } } : {}),
          }
        : false,
      // RAW audio on purpose (all browser DSP off): this capture only feeds
      // the level meter / device labels and the self-listen monitor — it is
      // never published (the publish path acquires its own capture with the
      // user's real EC/NS constraints in App.js). Keeping EC off here also
      // stops a concurrent EC'd capture from letting the echo canceller
      // cancel the self-listen playback (sustained notes fading to zero).
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        ...(exact && audioDeviceId ? { deviceId: { exact: audioDeviceId } } : {}),
      },
    })
    try {
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia(build(true))
      } catch (exactErr) {
        // A user-initiated mic change must fail loudly, not silently fall back to
        // a different device — the caller reverts the dropdown and re-acquires.
        if (strictAudio) return null
        // Stale/unplugged device → retry letting the browser pick defaults.
        console.warn("⚠️ device setup: exact deviceId failed, retrying default:", exactErr.message)
        stream = await navigator.mediaDevices.getUserMedia(build(false))
      }
      setPreview(stream)
      return stream
    } catch (err) {
      console.warn("⚠️ device setup: could not get media:", err.message)
      setError(
        "Couldn't access your camera/microphone. Check browser permissions — you can still enter with your webcam off."
      )
      setPreview(null)
      return null
    }
  }, [])

  // Enumerate devices (labels are only populated after permission is granted,
  // hence we acquire a stream first). Resolve the active deviceIds so the
  // dropdowns reflect what's actually live.
  const refreshDevices = useCallback(async (activeStream, preferVid, preferAud) => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const cams = devices.filter((d) => d.kind === "videoinput")
      const mics = devices.filter((d) => d.kind === "audioinput")
      setVideoInputs(cams)
      setAudioInputs(mics)
      const activeVid = activeStream?.getVideoTracks?.()[0]?.getSettings?.().deviceId
      const activeAud = activeStream?.getAudioTracks?.()[0]?.getSettings?.().deviceId
      // Prefer the saved id ONLY if it's still enumerable — otherwise it's a
      // stale/rotated id and honoring it would leave the dropdown + saved value
      // pointing at a different device than the one the preview actually opened
      // (acquire fell back to a default). Fall back to the live track's device,
      // then the first camera. Keeps preview ⇄ dropdown ⇄ saved value in sync.
      const preferVidValid = preferVid && cams.some((d) => d.deviceId === preferVid)
      const preferAudValid = preferAud && mics.some((d) => d.deviceId === preferAud)
      setVideoDeviceId((preferVidValid && preferVid) || activeVid || cams[0]?.deviceId || "")
      setAudioDeviceId((preferAudValid && preferAud) || activeAud || mics[0]?.deviceId || "")
    } catch (err) {
      console.warn("⚠️ device setup: enumerateDevices failed:", err.message)
    }
  }, [])

  // On open: pre-fill from saved settings, acquire a preview, then list devices.
  // On close/unmount: release the camera/mic.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const initVid = initialSettings?.videoDeviceId || ""
    const initAud = initialSettings?.audioDeviceId || ""
    const initVideoOn = initialSettings?.videoOn !== false // default ON
    const initMicOn = initialSettings?.micOn !== false // default ON
    setVideoOn(initVideoOn)
    setMicOn(initMicOn)
    // Reflect the saved preference so reopening the dialog shows the box as the
    // user last left it (was hard-reset to false before).
    setDontShowAgain(initialSettings?.dontShowAgain === true)
    ;(async () => {
      let stream
      if (iosLive) {
        // Borrow the live self-stream as the preview — no new camera capture.
        ownsPreviewRef.current = false
        previewRef.current = activeStream
        setPreviewStream(activeStream)
        stream = activeStream
      } else {
        stream = await acquire({
          videoDeviceId: initVid,
          audioDeviceId: initAud,
          videoOn: initVideoOn,
        })
        ownsPreviewRef.current = !!stream
      }
      if (cancelled) {
        // Only stop a stream WE acquired; never the borrowed activeStream.
        if (ownsPreviewRef.current) stopStream(stream)
        return
      }
      // enumerateDevices() doesn't open a camera, so it's safe even on iOS-live.
      await refreshDevices(stream, initVid, initAud)
    })()
    return () => {
      cancelled = true
      if (ownsPreviewRef.current) stopStream(previewRef.current)
      previewRef.current = null
      ownsPreviewRef.current = false
      setPreviewStream(null)
    }
    // Re-run only when the modal opens. initialSettings is read as a snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleCameraChange = async (id) => {
    const prev = videoDeviceId
    setVideoDeviceId(id)
    // iOS-live: don't preview the chosen camera (a second getUserMedia would
    // steal the single iOS camera from the live producer). Just record the
    // choice; App's handleSwapDevices does the real switch on confirm.
    if (iosLive) return
    const stream = await acquire({ videoDeviceId: id, audioDeviceId, videoOn })
    if (!stream || !videoOn) return
    // acquire()'s stale-id fallback can silently hand back a different camera
    // (e.g. the previous one when the chosen camera is busy). Keep the dropdown
    // honest: snap it back to the device that actually opened and say why.
    const actual = stream.getVideoTracks()[0]?.getSettings?.().deviceId
    if (actual && actual !== id) {
      setVideoDeviceId(videoInputs.some((d) => d.deviceId === actual) ? actual : prev)
      setError("Couldn't switch to that camera — it may be in use by another app.")
    }
  }

  const handleMicChange = async (id) => {
    const prev = audioDeviceId
    setAudioDeviceId(id)
    if (iosLive) return // see handleCameraChange — defer to confirm
    const stream = await acquire({ videoDeviceId, audioDeviceId: id, videoOn, strictAudio: true })
    if (stream) return
    // The chosen mic couldn't open. Restore the previous selection's preview
    // (acquire() already stopped it), snap the dropdown back, and say why.
    // Re-acquire FIRST — acquire() clears the error state on entry.
    setAudioDeviceId(prev)
    await acquire({ videoDeviceId, audioDeviceId: prev, videoOn })
    setError("Couldn't switch to that microphone — it may be unavailable or in use.")
  }

  const handleToggleVideo = async () => {
    const next = !videoOn
    setVideoOn(next)
    if (iosLive) return // see handleCameraChange — defer to confirm
    await acquire({ videoDeviceId, audioDeviceId, videoOn: next })
  }

  // The mic toggle is intent only — we don't play the mic back in the preview,
  // so there's nothing to re-acquire. It decides whether audio starts live on
  // entry (App pauses the audio producer when micOn is false).
  const handleToggleMic = () => setMicOn((v) => !v)

  const handleRefresh = () =>
    refreshDevices(previewRef.current, videoDeviceId, audioDeviceId)

  // Clamp a Select value to an id that actually exists in the options. The
  // active track's getSettings().deviceId can differ from every enumerated
  // deviceId (browser quirk: "default" pseudo-device, per-API hashing, or a
  // stale saved id after a device unplug), which makes MUI's controlled Select
  // warn about an out-of-range value. Fall back to the first option (or "").
  const safeValue = (id, options) =>
    options.some((d) => d.deviceId === id) ? id : options[0]?.deviceId || ""

  const release = () => {
    // Never stop the borrowed live activeStream (iOS-live preview) — it's owned
    // by mediaService and the producer is still using it.
    if (ownsPreviewRef.current) stopStream(previewRef.current)
    previewRef.current = null
    ownsPreviewRef.current = false
    setPreviewStream(null)
  }

  const handleOk = () => {
    release()
    // Persist the CLAMPED ids (guaranteed to exist in the enumerated list) plus
    // each device's label. The label is the stable key the publish path uses to
    // re-resolve a deviceId that the browser later rotates (see App.js
    // resolveDeviceId) — without it, a rotated id is unrecoverable and we'd
    // overconstrain on every reload.
    const vId = safeValue(videoDeviceId, videoInputs)
    const aId = safeValue(audioDeviceId, audioInputs)
    const vLabel = videoInputs.find((d) => d.deviceId === vId)?.label || ""
    const aLabel = audioInputs.find((d) => d.deviceId === aId)?.label || ""
    onConfirm &&
      onConfirm({
        videoDeviceId: vId,
        audioDeviceId: aId,
        videoLabel: vLabel,
        audioLabel: aLabel,
        videoOn,
        micOn,
        dontShowAgain: showDontShowAgain ? dontShowAgain : false,
      })
  }

  const handleDismiss = () => {
    release()
    onClose && onClose()
  }

  const deviceLabel = (d, i, kind) => d.label || `${kind} ${i + 1}`

  const videoValue = safeValue(videoDeviceId, videoInputs)
  const audioValue = safeValue(audioDeviceId, audioInputs)

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle>Set up your camera &amp; mic</DialogTitle>
      <DialogContent>
        <div className={classes.preview}>
          {videoOn && previewStream ? (
            <PreviewVideo stream={previewStream} className={classes.previewVideo} />
          ) : (
            <div className={classes.previewPlaceholder}>
              {videoOn ? "Starting camera…" : "Camera off"}
            </div>
          )}
        </div>

        {error && <Typography className={classes.errorText}>{error}</Typography>}

        <div className={classes.deviceRow}>
          <VideocamIcon className={classes.deviceIcon} fontSize="small" />
          <Select
            className={classes.select}
            variant="outlined"
            margin="dense"
            displayEmpty
            value={videoValue}
            onChange={(e) => handleCameraChange(e.target.value)}
          >
            {videoInputs.length === 0 && (
              <MenuItem value="">No cameras found</MenuItem>
            )}
            {videoInputs.map((d, i) => (
              <MenuItem key={d.deviceId} value={d.deviceId}>
                {deviceLabel(d, i, "Camera")}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title="Refresh device list">
            <IconButton className={classes.refreshBtn} size="small" onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <div className={classes.deviceRow}>
          <MicIcon className={classes.deviceIcon} fontSize="small" />
          <Select
            className={classes.select}
            variant="outlined"
            margin="dense"
            displayEmpty
            value={audioValue}
            onChange={(e) => handleMicChange(e.target.value)}
          >
            {audioInputs.length === 0 && (
              <MenuItem value="">No microphones found</MenuItem>
            )}
            {audioInputs.map((d, i) => (
              <MenuItem key={d.deviceId} value={d.deviceId}>
                {deviceLabel(d, i, "Microphone")}
              </MenuItem>
            ))}
          </Select>
        </div>

        {/* Advanced: the full mic-processing panel (same controls as
            Settings → Voice & Video), collapsed by default. Reuses THIS
            modal's capture via a cloned track — no second getUserMedia (iOS
            single-capture safe). */}
        {micProcessing && onMicProcessingChange && (
          <>
            <div
              className={classes.advancedToggle}
              role="button"
              tabIndex={0}
              onClick={() => setAdvancedOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setAdvancedOpen((v) => !v)
              }}
            >
              {advancedOpen ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
              <span>Advanced — microphone processing</span>
            </div>
            <Collapse in={advancedOpen} mountOnEnter unmountOnExit>
              <div className={classes.advancedBody}>
                <MicProcessingPanel
                  settings={micProcessing}
                  onChange={onMicProcessingChange}
                  isPublishing={isPublishing}
                  previewStream={previewStream}
                  savedAudioDeviceId={initialSettings?.audioDeviceId || ""}
                />
              </div>
            </Collapse>
          </>
        )}

        {/* Live mic level so the user can see the chosen mic actually picks
            them up before joining. Meters the RAW preview capture; the ear
            button opens a dedicated EC-free monitor (createSelfListenMonitor:
            echo-cancellation would pump against its own playback, and the
            outbound mic is paused so the test never broadcasts). Hidden while
            Advanced is expanded — the panel below has its own (post-chain)
            meter and ear button, and two stacked meters read as a bug. */}
        {!advancedOpen && (
          <div className={classes.deviceRow}>
            <GraphicEqIcon className={classes.deviceIcon} fontSize="small" />
            <MicLevelMeter
              stream={previewStream}
              getListenStream={handleQuickListen}
              onListenStop={handleQuickListenStop}
            />
          </div>
        )}

        <div className={classes.toggleRow}>
          {videoOn ? (
            <VideocamIcon className={classes.deviceIcon} fontSize="small" />
          ) : (
            <VideocamOffIcon className={classes.deviceIcon} fontSize="small" />
          )}
          <div className={classes.toggleLabel}>
            <span className={classes.toggleTitle}>Camera</span>
            <span className={classes.toggleSub}>
              {videoOn ? "Joins with your camera live" : "Joins with your camera off"}
            </span>
          </div>
          <Switch
            checked={videoOn}
            onChange={handleToggleVideo}
            color="primary"
            slotProps={{ input: { "aria-label": "Start with camera on" } }}
          />
        </div>

        <div className={classes.toggleRow}>
          {micOn ? (
            <MicIcon className={classes.deviceIcon} fontSize="small" />
          ) : (
            <MicOffIcon className={classes.deviceIcon} fontSize="small" />
          )}
          <div className={classes.toggleLabel}>
            <span className={classes.toggleTitle}>Microphone</span>
            <span className={classes.toggleSub}>
              {micOn ? "Joins with your mic live" : "Joins muted — unmute anytime"}
            </span>
          </div>
          <Switch
            checked={micOn}
            onChange={handleToggleMic}
            color="primary"
            slotProps={{ input: { "aria-label": "Start with microphone on" } }}
          />
        </div>

        {showDontShowAgain && (
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                color="primary"
              />
            }
            label="Don't show this again on join"
          />
        )}

        <Typography className={classes.accessNote}>
          You can reopen this anytime from the&nbsp;
          <strong>gear icon</strong> on the Webcam panel.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} color="inherit">
          {reopened ? "Cancel" : "Skip"}
        </Button>
        <Button onClick={handleOk} color="primary" variant="contained">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeviceSetupModal
